import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useMembership } from "@/context/MembershipContext";
import { Barbell, SignOut, ChartLine, ListChecks, Users, ChatCircle, Notebook, House, Lock, Lightning, Gift, ForkKnife, UserPlus, Robot, Heart, Target, CalendarBlank, CurrencyDollar, User } from "@phosphor-icons/react";
import NotificationPermissionPrompt from "@/components/NotificationPermissionPrompt";
import MobileBottomNav from "@/components/MobileBottomNav";

const coachLinks = [
  { to: "/coach", label: "Overview", icon: House, end: true, testid: "nav-coach-overview" },
  { to: "/coach/revenue", label: "Revenue", icon: CurrencyDollar, testid: "nav-coach-revenue" },
  { to: "/coach/clients", label: "Clients", icon: Users, testid: "nav-coach-clients" },
  { to: "/coach/sunday-checkins", label: "Sunday Inbox", icon: CalendarBlank, testid: "nav-coach-sunday-checkins" },
  { to: "/coach/weekly-report", label: "Weekly Report", icon: Target, testid: "nav-coach-weekly-report" },
  { to: "/coach/waitlist", label: "Waitlist", icon: UserPlus, testid: "nav-coach-waitlist" },
  { to: "/coach/programs", label: "Workouts", icon: ListChecks, testid: "nav-coach-programs" },
  { to: "/coach/meals", label: "Meal Library", icon: ForkKnife, testid: "nav-coach-meals" },
  { to: "/coach/ai-log", label: "AI Q&A", icon: Robot, testid: "nav-coach-ai-log" },
  { to: "/coach/motivation-wall", label: "Motivation Wall", icon: Heart, testid: "nav-coach-motivation-wall" },
];

const clientLinks = [
  { to: "/app", label: "Today", icon: House, end: true, testid: "nav-client-today" },
  { to: "/app/program", label: "Program", icon: Barbell, testid: "nav-client-program" },
  { to: "/app/meals", label: "Meals", icon: ForkKnife, testid: "nav-client-meals" },
  { to: "/app/stats", label: "My Stats", icon: ChartLine, testid: "nav-client-stats" },
  { to: "/app/progress", label: "Weight Log", icon: ChartLine, testid: "nav-client-progress" },
  { to: "/app/logs", label: "Workout Logs", icon: Notebook, testid: "nav-client-logs" },
  { to: "/app/messages", label: "Coach", icon: ChatCircle, testid: "nav-client-messages" },
  { to: "/app/refer", label: "Refer & Earn", icon: Gift, testid: "nav-client-refer" },
  { to: "/app/account", label: "Account", icon: User, testid: "nav-client-account" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { membership } = useMembership();
  const navigate = useNavigate();
  const links = user?.role === "coach" ? coachLinks : clientLinks;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#0A0A0A] text-white">
      {/* Sidebar — desktop only for clients (bottom nav replaces on mobile).
          Coaches still get the horizontal top nav on mobile since bottom nav
          would only fit ~5 tabs and coach has 6+ pages. */}
      <aside className={`md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-[#262626] bg-[#0A0A0A] md:flex md:flex-col ${user?.role === "client" ? "hidden md:flex" : "flex"}`}>
        <div className="px-6 py-6 border-b border-[#262626] hidden md:block">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF3333] flex items-center justify-center">
              <Barbell weight="bold" size={20} color="#fff" />
            </div>
            <div>
              <div className="font-oswald text-xl font-bold uppercase tracking-wider leading-none">Lazy Lean Method<sup className="font-manrope text-[11px] font-semibold tracking-normal ml-0.5 -top-1.5 relative">™</sup></div>
              <div className="id-label mt-1">Coaching Portal</div>
            </div>
          </div>
        </div>

        {/* mobile compact header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 w-full">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FF3333] flex items-center justify-center">
              <Barbell weight="bold" size={16} color="#fff" />
            </div>
            <span className="font-oswald text-lg font-bold uppercase tracking-wider">Lazy Lean Method<sup className="font-manrope text-[11px] font-semibold tracking-normal ml-0.5 -top-1.5 relative">™</sup></span>
          </div>
          <button onClick={handleLogout} data-testid="logout-button-mobile" className="text-zinc-400 hover:text-white">
            <SignOut size={20} />
          </button>
        </div>

        <nav className="flex md:flex-col gap-1 px-2 md:px-3 py-2 md:py-4 overflow-x-auto md:overflow-x-visible flex-1">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm font-manrope uppercase tracking-[0.15em] whitespace-nowrap transition-colors duration-150 border-l-2 ${
                    isActive
                      ? "bg-[#1A1A1A] text-white border-[#FF3333]"
                      : "text-zinc-400 hover:text-white hover:bg-[#121212] border-transparent"
                  }`
                }
              >
                <Icon size={18} weight="bold" />
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden md:block border-t border-[#262626] p-4">
          {user?.role === "client" && membership && (
            <NavLink
              to="/app/subscribe"
              data-testid="membership-badge"
              className={`block mb-3 px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold border ${
                membership.active
                  ? "border-[#FF3333]/40 bg-[#FF3333]/5 text-[#FF3333]"
                  : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500"
              }`}
            >
              {membership.active ? (
                <span className="flex items-center gap-2"><Lightning size={12} weight="fill" /> {membership.days_remaining}d left</span>
              ) : (
                <span className="flex items-center gap-2"><Lock size={12} weight="bold" /> Membership · Inactive</span>
              )}
            </NavLink>
          )}
          <div className="id-label mb-1">Signed in</div>
          <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-zinc-500 truncate mb-3" data-testid="current-user-email">{user?.email}</div>
          <button onClick={handleLogout} data-testid="logout-button" className="id-btn-secondary w-full text-xs">
            <SignOut size={14} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar — clients only. Replaces the hidden sidebar so
          they still see the logo + can reach Account + log out. */}
      {user?.role === "client" && (
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[#262626] bg-[#0A0A0A] sticky top-0 z-20">
          <NavLink to="/app" className="flex items-center gap-2" data-testid="mobile-home-logo">
            <div className="w-7 h-7 bg-[#FF3333] flex items-center justify-center">
              <Barbell weight="bold" size={16} color="#fff" />
            </div>
            <span className="font-oswald text-base font-bold uppercase tracking-wider">Lazy Lean Method<sup className="font-manrope text-[10px] font-semibold tracking-normal ml-0.5 -top-1.5 relative">™</sup></span>
          </NavLink>
          <div className="flex items-center gap-4">
            <NavLink
              to="/app/account"
              data-testid="mobile-account-link"
              className={({ isActive }) => "text-zinc-400 hover:text-white " + (isActive ? "text-[#FF3333]" : "")}
              aria-label="Account"
              title="Account"
            >
              <User size={20} weight="bold" />
            </NavLink>
            <button onClick={handleLogout} data-testid="logout-button-mobile-top" className="text-zinc-400 hover:text-white" aria-label="Sign out">
              <SignOut size={20} />
            </button>
          </div>
        </div>
      )}

      <main className={`flex-1 min-w-0 ${user?.role === "client" ? "pb-20 md:pb-0" : ""}`}>{children}</main>
      {user?.role === "client" && <NotificationPermissionPrompt />}
      {user?.role === "client" && <MobileBottomNav />}
    </div>
  );
}
